import { ChevronRight } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { cn } from '../lib/cn'
import { PageIcon } from './page-icon'

export interface PageHeaderProps {
  title: ReactNode
  /** Beside the title (e.g. the document stamp). */
  status?: ReactNode
  /** Under the title: secondary name, counts, a short description. */
  meta?: ReactNode
  actions?: ReactNode
  /** The section's icon before the title (from the sidebar entry the page belongs to); off for pages that draw their own. */
  icon?: boolean
  className?: string
}

/**
 * Page title row: the only place (with the wordmark) that uses the display face. The icon of the part
 * of the app the page belongs to stands in a tile of the accent before it (`PageIcon`).
 */
export function PageHeader({ title, status, meta, actions, icon = true, className }: PageHeaderProps) {
  return (
    <div className={cn('flex flex-wrap items-start justify-between gap-x-4 gap-y-3', className)}>
      <div className="flex min-w-0 items-center gap-3">
        {icon ? <PageIcon /> : null}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <h1 data-slot="page-title" className="font-display text-xl font-medium text-ink">
              {title}
            </h1>
            {status}
          </div>
          {meta ? <div className="mt-0.5 text-sm text-ink-muted">{meta}</div> : null}
        </div>
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  )
}

export function Toolbar({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('flex flex-wrap items-center gap-2', className)} {...props} />
}

/** Page padding and max width shared by every screen. */
export function Page({
  className,
  width = 'full',
  ...props
}: ComponentProps<'div'> & { width?: 'full' | 'form' }) {
  return (
    <div
      className={cn(
        'mx-auto w-full px-4 py-4 sm:px-6 sm:py-5',
        width === 'form' ? 'max-w-[912px]' : 'max-w-[1600px]',
        className,
      )}
      {...props}
    />
  )
}

export function BreadcrumbList({ label, children }: { label: string; children: ReactNode }) {
  return (
    <nav aria-label={label} className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1 text-sm">{children}</ol>
    </nav>
  )
}

export function BreadcrumbItem({
  children,
  current = false,
  separator = true,
  className,
}: {
  children: ReactNode
  current?: boolean
  separator?: boolean
  className?: string
}) {
  return (
    <li className={cn('flex min-w-0 items-center gap-1', className)}>
      {separator ? (
        <ChevronRight
          aria-hidden
          className="hidden size-3.5 shrink-0 text-ink-faint md:block rtl:-scale-x-100"
        />
      ) : null}
      <span
        aria-current={current ? 'page' : undefined}
        className={cn('truncate', current ? 'font-medium text-ink' : 'text-ink-muted')}
      >
        {children}
      </span>
    </li>
  )
}

export interface DefinitionItem {
  label: ReactNode
  value: ReactNode
  /** Span both columns (long text such as an address or notes). */
  wide?: boolean
}

/**
 * Read-only fields on detail pages. Empty values show a dash. Text values are what users typed
 * (an address, a company), so they are direction-isolated (`dir="auto"`): English in an Arabic page
 * keeps its punctuation at the right end, and the value stays aligned under its label.
 */
export function DefinitionList({ items, className }: { items: DefinitionItem[]; className?: string }) {
  return (
    <dl className={cn('grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2', className)}>
      {items.map((item, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static, order-stable list
        <div key={index} className={cn('min-w-0', item.wide && 'sm:col-span-2')}>
          <dt className="text-xs text-ink-muted">{item.label}</dt>
          <dd
            dir={typeof item.value === 'string' ? 'auto' : undefined}
            className={cn(
              'mt-0.5 text-base break-words whitespace-pre-line text-ink',
              // As wide as the text: the box stays under its label whatever the text's direction.
              typeof item.value === 'string' && 'w-fit max-w-full',
            )}
          >
            {item.value === null || item.value === undefined || item.value === '' ? (
              <span className="text-ink-muted">—</span>
            ) : (
              item.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function Panel({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('rounded-panel border border-rule bg-surface panel', className)} {...props} />
}
